import axios from 'axios'
import { API_URL, getAuthHeader } from '@/services/api.service.js'
import { axiosCacheClient } from '@/services/axios-cache-client'
import { logger } from '@/utils/logger'

export async function getRateCardMapping({ boardId }) {
  // GET /settings/:boardId/rate-card-mapping
  try {
    const response = await axios.get(`${await API_URL()}/settings/${boardId}/rate-card-mapping`, {
      headers: { ...(await getAuthHeader()) }
    })

    return response?.data
  } catch (error) {
    logger.error('Error fetching rate card mapping:', error.message)
    return null
  }
}

export async function getRateCardMappingRow({ boardId, attributeColumnValues }) {
  // GET /settings/:boardId/rate-card-mapping-row
  try {
    const response = await axios.get(
      `${await API_URL()}/settings/${boardId}/rate-card-mapping-row`,
      {
        params: { attributeColumnValues },
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(response.statusText)
    }
  } catch (error) {
    if (error.response?.status !== 404) {
      logger.error('Error fetching rate card mapping row:', error.message)
    }
    return error.response || error
  }
}

export async function getRateCardMappingRows({ boardId, planBoardId, lookups }) {
  // POST /settings/:boardId/rate-card-mapping-rows
  try {
    const response = await axios.post(
      `${await API_URL()}/settings/${boardId}/rate-card-mapping-rows`,
      { data: { lookups, planBoardId } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(response.statusText)
    }
  } catch (error) {
    logger.error('Error fetching rate card mapping rows:', error.message)
    return error.response?.data || { success: false, results: [], message: error.message }
  }
}

export async function getRateCardMappingShortlist({
  boardId,
  shortlistBy,
  rateCardAttributeColumns
}) {
  // GET /settings/:boardId/rate-card-mapping-shortlist
  try {
    const response = await axios.get(
      `${await API_URL()}/settings/${boardId}/rate-card-mapping-shortlist`,
      {
        params: { shortlistBy, rateCardAttributeColumns },
        headers: { ...(await getAuthHeader()) }
      }
    )

    return response?.data || {}
  } catch (error) {
    logger.error('Error fetching rate card mapping shortlist:', error.message)
    return null
  }
}

export async function getJobCatalogMapping({ boardId }) {
  // GET /settings/:boardId/job-catalog-mapping
  try {
    const response = await axios.get(`${await API_URL()}/settings/${boardId}/job-catalog-mapping`, {
      headers: { ...(await getAuthHeader()) }
    })

    return response?.data
  } catch (error) {
    logger.error('Error fetching job catalog mapping:', error.message)
    return null
  }
}

export async function getJobCatalogMappingRow({ boardId, pivotValue }) {
  // GET /settings/:boardId/job-catalog-mapping-row
  try {
    const response = await axios.get(
      `${await API_URL()}/settings/${boardId}/job-catalog-mapping-row`,
      {
        params: { pivotValue },
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(response.statusText)
    }
  } catch (error) {
    if (error.response?.status !== 404) {
      logger.error('Error fetching job catalog mapping row:', error.message)
    }
    return error.response || error
  }
}

export async function getJobCatalogTitleDescriptionRow({
  boardId,
  pivotColumnValue,
  jobTitleColumnValue
}) {
  // GET /settings/:boardId/job-catalog-title-description-row
  try {
    const response = await axios.get(
      `${await API_URL()}/settings/${boardId}/job-catalog-title-description-row`,
      {
        params: { pivotColumnValue, jobTitleColumnValue },
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(response.statusText)
    }
  } catch (error) {
    // 404 and 400 are expected when job catalog isn't configured or has missing data
    if (error.response?.status === 404 || error.response?.status === 400) {
      return null
    }
    logger.error('Error fetching job catalog title and description row:', error.message)
    throw error
  }
}

export async function getSettings({ boardId }) {
  // GET /settings/:boardId
  try {
    const ret = await axiosCacheClient.get(`${await API_URL()}/settings/${boardId}`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return {}
  }
}

export async function updateSettings({ boardId, settings }) {
  try {
    const ret = await axios.post(
      `${await API_URL()}/settings/${boardId}`,
      { data: { settings } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200 || ret.status === 201) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return null
  }
}

export async function flushCache({ boardId }) {
  try {
    logger.info('Flushing cache... BoardId:', boardId)
    const ret = await axios.post(
      `${await API_URL()}/settings/flush-cache`,
      { boardId },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      logger.info('Cache flushed successfully')
      return ret.data
    } else {
      logger.error('Failed to flush cache')
      throw new Error(ret.statusText)
    }
  } catch (error) {
    logger.error('Error flushing cache:', error.message)
    return null
  }
}

export async function flushDbCache({ boardId, cacheType }) {
  try {
    logger.info('Flushing database cache... BoardId:', boardId, 'CacheType:', cacheType)
    const ret = await axios.post(
      `${await API_URL()}/settings/flush-db-cache`,
      { boardId, cacheType },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      logger.info('Database cache flushed successfully')
      return ret.data
    } else {
      logger.error('Failed to flush database cache')
      throw new Error(ret.statusText)
    }
  } catch (error) {
    logger.error('Error flushing database cache:', error.message)
    return null
  }
}

export async function uploadLogo({ boardId, logo }) {
  const formData = new FormData()
  formData.append('logo', logo)

  try {
    await axios.post(`${await API_URL()}/settings/${boardId}/logo`, formData, {
      headers: {
        ...(await getAuthHeader())
      }
    })
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to upload logo')
  }
}

/**
 * Save the employee ID template for auto-generating employee IDs in scenarios.
 * @param {Object} params - Parameters
 * @param {string} params.boardId - The board ID
 * @param {string|null} params.template - The template string (e.g., 'EMP_{N}') or null to disable
 * @returns {Promise<Object|null>} Response data or null on error
 */
export async function saveEmployeeIdTemplate({ boardId, template }) {
  try {
    const ret = await axios.post(
      `${await API_URL()}/settings/${boardId}`,
      { data: { settings: { employeeIdTemplate: template } } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200 || ret.status === 201) {
      return ret.data
    } else {
      throw new Error(ret.statusText)
    }
  } catch (error) {
    logger.error('Error saving employee ID template:', error.message)
    throw error
  }
}

/**
 * Save the Dayforce auto-sync configuration for a main board.
 *
 * Unlike updateSettings this lets the error propagate, because the backend rejects an unauthorized
 * or malformed schedule and the caller has to surface that instead of showing a stale toggle.
 *
 * @param {object} params - Parameters
 * @param {string} params.boardId - The main board ID
 * @param {boolean} params.isDayforceAutoSyncOn - Whether scheduled refreshes are enabled
 * @param {object} [params.dayforceAutoSyncOptions] - The UTC schedule; omit to keep the stored one
 * @returns {Promise<object>} Response data
 */
export async function saveDayforceAutoSync({
  boardId,
  isDayforceAutoSyncOn,
  dayforceAutoSyncOptions
}) {
  const settings = { isDayforceAutoSyncOn }
  if (dayforceAutoSyncOptions) {
    settings.dayforceAutoSyncOptions = dayforceAutoSyncOptions
  }

  const ret = await axios.post(
    `${await API_URL()}/settings/${boardId}`,
    { data: { settings } },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200 || ret.status === 201) {
    return ret.data
  }
  throw new Error(ret.statusText)
}

// Cache for calculated fields (fetched once from backend)
let calculatedFieldsCache = null

/**
 * Get the list of toggleable calculated fields from backend (source of truth)
 * Results are cached after first fetch since these don't change at runtime
 */
export async function getCalculatedFields() {
  if (calculatedFieldsCache) {
    return calculatedFieldsCache
  }

  try {
    const response = await axios.get(`${await API_URL()}/settings/calculated-fields`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (response.status === 200) {
      calculatedFieldsCache = response.data?.data?.calculatedFields || []
      return calculatedFieldsCache
    }
    return []
  } catch (error) {
    logger.error('Error fetching calculated fields:', error.message)
    return []
  }
}

export async function getCalculatedFieldVisibility({ boardId }) {
  try {
    const response = await axios.get(
      `${await API_URL()}/settings/${boardId}/calculated-field-visibility`,
      { headers: { ...(await getAuthHeader()) } }
    )

    if (response.status === 200) {
      return response.data?.data?.calculatedFieldVisibility || {}
    }
    return {}
  } catch (error) {
    logger.error('Error fetching calculated field visibility:', error.message)
    return {}
  }
}

export async function getCalculatedFieldAuditLogs({ boardId }) {
  try {
    const response = await axios.get(
      `${await API_URL()}/settings/${boardId}/calculated-field-audit-logs`,
      { headers: { ...(await getAuthHeader()) } }
    )

    if (response.status === 200) {
      return response.data?.data?.logs || []
    }
    return []
  } catch (error) {
    logger.error('Error fetching calculated field audit logs:', error.message)
    return []
  }
}

export async function updateCalculatedFieldVisibility({
  boardId,
  calculatedFieldVisibility,
  changedFields
}) {
  try {
    const response = await axios.post(
      `${await API_URL()}/settings/${boardId}/calculated-field-visibility`,
      { data: { calculatedFieldVisibility, changedFields } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (response.status === 200) {
      return response.data?.data?.calculatedFieldVisibility || {}
    }
    throw new Error(response.statusText)
  } catch (error) {
    logger.error('Error updating calculated field visibility:', error.message)
    throw error
  }
}

// Fields Mapping API
export async function getFieldsMapping({ boardId }) {
  // GET /settings/:boardId/fields-mapping
  try {
    const response = await axios.get(`${await API_URL()}/settings/${boardId}/fields-mapping`, {
      headers: { ...(await getAuthHeader()) }
    })

    return response?.data
  } catch (error) {
    logger.error('Error fetching fields mapping:', error.message)
    return null
  }
}

export async function deleteFieldsMapping({ boardId, mappingId }) {
  // DELETE /settings/:boardId/fields-mapping/:mappingId
  try {
    const response = await axios.delete(
      `${await API_URL()}/settings/${boardId}/fields-mapping/${mappingId}`,
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    return response?.data
  } catch (error) {
    logger.error('Error deleting fields mapping:', error.message)
    return null
  }
}

export async function updateFieldsMapping({ boardId, mappingId, data }) {
  // PATCH /settings/:boardId/fields-mapping/:mappingId
  try {
    const response = await axios.patch(
      `${await API_URL()}/settings/${boardId}/fields-mapping/${mappingId}`,
      data,
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    return response?.data
  } catch (error) {
    logger.error('Error updating fields mapping:', error.message)
    return null
  }
}

export async function getFieldsMappingDetail({ boardId, mappingId }) {
  // GET /settings/:boardId/fields-mapping/:mappingId
  try {
    const response = await axios.get(
      `${await API_URL()}/settings/${boardId}/fields-mapping/${mappingId}`,
      {
        headers: { ...(await getAuthHeader()) }
      }
    )
    return response?.data || {}
  } catch (error) {
    logger.error('Error fetching fields mapping detail:', error.message)
    return { success: false, message: error.message }
  }
}

/**
 * Unified lookup - auto-discovers relevant mappings by boardId
 * @param {Object} params
 * @param {string} params.boardId - Board ID (can be scenario)
 * @param {Object} params.lookup - { peopleIds: [...], changedField: { name, value } }
 * @returns {Promise<Object>} { success: true, results: [{ personId, mappings: [{ mappingId, data }] }] }
 */
export async function lookupFieldsMapping({ boardId, lookup }) {
  // POST /settings/:boardId/fields-mapping/lookup (unified endpoint)
  try {
    const response = await axios.post(
      `${await API_URL()}/settings/${boardId}/fields-mapping/lookup`,
      { lookup },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(response.statusText)
    }
  } catch (error) {
    if (error.response?.status !== 404) {
      logger.error('Error looking up fields mapping:', error.message)
    }
    return error.response?.data || { success: false, message: error.message }
  }
}
